import React, { useMemo, useState } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { GlobalStyles } from '@mui/material'
import { Responsive, WidthProvider, Layouts, Layout } from 'react-grid-layout'
import { TELEMETRY_API } from '@/constant/constants'

import WeatherStationCard, {
  WeatherStation,
  GlassCard,
  selectWeatherIcon,
} from './Components/WeatherStationCard'
import { useAppSelector } from '@/store/stores'
import { srcInterfaceState } from '@store/slices/srcInterfaceSlice'

import SoilMoistureHeatmapCard from './Components/SoilMoistureHeatmapCard'

import RadarIframeCard from './Components/RadarIframeCard'

const ResponsiveGridLayout = WidthProvider(
  Responsive as any,
) as unknown as React.ComponentType<any>

/**
 * CustomSentosaInsightDashboard — MUI version (no Tailwind)
 * ---------------------------------------------------------
 * Grid rules:
 *  - Keeps widget sizes via grid spans
 *  - Reflows columns: 3 (xs) / 4 (md) / 5 (xl)
 *  - Dense packing to minimize gaps
 */

// ---- TelemetryHub API shapes & icon selector ----
interface HubPoint {
  time: string
  temp_out?: number
  hum_out?: number
  et?: number
  wind_speed_avg?: number
  rainfall_mm?: number
  rain_rate_hi_mm?: number
  solar_rad_avg?: number
  wind_speed_hi?: number
}

// ---------------- Helpers (pure) ----------------
function formatMaybe(n?: number, suffix?: string): string | undefined {
  const sfx = typeof suffix === 'string' ? suffix : ''
  return typeof n === 'number' ? n.toFixed(1) + sfx : undefined
}

function formatFixed(n?: number, digits: number = 1): string | undefined {
  return typeof n === 'number' ? n.toFixed(digits) : undefined
}

// ---- Weather stations (live fetch from TelemetryHub) ----
function iso(d: Date) {
  return d.toISOString()
}
function sum(arr: number[]) {
  return arr.reduce((a, b) => a + b, 0)
}

const WS_NAMES = [
  'temp_out',
  'hum_out',
  'et',
  'wind_speed_avg',
  'rainfall_mm',
  'rain_rate_hi_mm',
  'solar_rad_avg',
  'wind_speed_hi',
]

async function fetchStation24h(
  assetId: number,
  stationName: string,
): Promise<WeatherStation> {
  const now = new Date()
  const from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
  const params = new URLSearchParams({
    AssetId: String(assetId),
    PageSize: '1000',
    PageIndex: '1',
    FromTime: iso(from),
    ToTime: iso(now),
    TimeBucket: '1',
  })
  WS_NAMES.forEach((n) => params.append('Names', n))
  const url = `${TELEMETRY_API}/api/Tag/list?${params.toString()}`
  const res = await fetch(url, {
    mode: 'cors',
    headers: {
      'X-API-Key': 'api-read-key-3456789012',
    },
  })
  if (!res.ok) throw new Error(`Fetch failed ${res.status}`)
  const json = (await res.json()) as { data: HubPoint[] }
  const rows = Array.isArray(json?.data) ? json.data : []
  rows.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
  const last = rows[rows.length - 1]
  const nowMs = now.getTime()
  const lastHourRows = rows.filter(
    (r) => new Date(r.time).getTime() >= nowMs - 60 * 60 * 1000,
  )

  const et24 = sum(rows.map((r) => r.et || 0))
  const rain24 = sum(rows.map((r) => r.rainfall_mm || 0))
  const rain1h = sum(lastHourRows.map((r) => r.rainfall_mm || 0))

  const condition = selectWeatherIcon({
    rainFall: last?.rainfall_mm,
    rainRateHigh: last?.rain_rate_hi_mm,
    solarRadAvg: last?.solar_rad_avg,
    windSpeedHigh: last?.wind_speed_hi,
    humidity: last?.hum_out,
    temperatureOutsideAvg: last?.temp_out,
  })

  return {
    id: String(assetId),
    name: stationName,
    temperatureC: last?.temp_out,
    humidityPct: last?.hum_out,
    windMs: last?.wind_speed_avg,
    condition,
    et24mm: et24,
    precip24mm: rain24,
    rainRateMmHr: last?.rain_rate_hi_mm,
    rain1hMm: rain1h,
  }
}

const STATION_CONFIG = [
  { id: 134, name: 'Serapong Hole 5' },
  { id: 133, name: 'Serapong Hole 9' },
  { id: 135, name: 'Tanjong Hole 18' },
]

// ---------------- Main Dashboard ----------------
export default function CustomSentosaInsightDashboard() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // Detect phone <600px
  const srcInterfaceStore = useAppSelector(srcInterfaceState)
  const bgUrl = srcInterfaceStore?.currentInterface?.pictureUrl

  const [liveStations, setLiveStations] = useState<WeatherStation[] | null>(
    null,
  )
  React.useEffect(() => {
    let alive = true

    async function loadStations() {
      try {
        const results = await Promise.all(
          STATION_CONFIG.map((s) => fetchStation24h(s.id, s.name)),
        )
        if (alive) setLiveStations(results)
      } catch (e) {
        if (alive) setLiveStations([])
      }
    }

    // Initial load
    loadStations()

    // Refresh every 5 minutes
    const interval = setInterval(
      () => {
        loadStations()
      },
      5 * 60 * 1000,
    )

    return () => {
      alive = false
      clearInterval(interval)
    }
  }, [])

  const initialLayouts: Layouts = {
    xl: [
      // Top row
      { i: 'radar', x: 0, y: 0, w: 9, h: 5 }, // Weather Radar (left, large)
      { i: 'soil', x: 9, y: 0, w: 3, h: 8 }, // Soil Heatmap (right)
      // Second row
      { i: 'ws1', x: 0, y: 5, w: 3, h: 3 }, // Weather Stations under radar
      { i: 'ws2', x: 3, y: 5, w: 3, h: 3 },
      { i: 'ws3', x: 6, y: 5, w: 3, h: 3 },
    ],
    md: [
      { i: 'radar', x: 0, y: 0, w: 9, h: 5 },
      { i: 'soil', x: 9, y: 0, w: 3, h: 4 },
      { i: 'ws1', x: 0, y: 5, w: 3, h: 3 },
      { i: 'ws2', x: 3, y: 5, w: 3, h: 3 },
      { i: 'ws3', x: 6, y: 5, w: 3, h: 3 },
    ],
    xs: [
      { i: 'radar', x: 0, y: 0, w: 12, h: 5 }, // Full width, taller for iframe visibility
      { i: 'soil', x: 0, y: 5, w: 12, h: 6 }, // Stack below, larger h for grid cells
      { i: 'ws1', x: 0, y: 11, w: 12, h: 5 }, // Larger h for metrics readability
      { i: 'ws2', x: 0, y: 16, w: 12, h: 5 },
      { i: 'ws3', x: 0, y: 21, w: 12, h: 5 },
    ],
  }

  const [layouts, setLayouts] = useState<Layouts>(() => {
    try {
      const saved = localStorage.getItem('cisgenx-dashboard-layouts')
      if (saved) {
        const parsed = JSON.parse(saved) as Layouts;
        return {
          ...parsed,
          xs: initialLayouts.xs, 
        };
      }
      return initialLayouts
    } catch {
      return initialLayouts
    }
  })

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [rowHeight, setRowHeight] = useState(57)
  const [containerHeight, setContainerHeight] = useState('calc(100vh - 43px)');

  React.useLayoutEffect(() => {
    if (!containerRef.current) return
    const el = containerRef.current
    const ro = new ResizeObserver(() => {
      // Enhanced responsive scaling: fit all widgets in both width and height
      const styles = window.getComputedStyle(el)
      const padTop = parseFloat(styles.paddingTop || '0') || 0
      const padBottom = parseFloat(styles.paddingBottom || '0') || 0
      const gridPadY = isXs ? 4 * 2 : 8 * 2
      const availH = Math.max(0, el.clientHeight - padTop - padBottom - gridPadY)
      const availW = el.clientWidth - (isXs ? 8 : 16)
      const marginY = isXs ? 4 : 8
      const marginX = isXs ? 4 : 8
      const currentBp = isXs ? 'xs' : window.innerWidth < 1280 ? 'md' : 'xl'
      const maxRows = (layouts[currentBp] || []).reduce((m, it) => Math.max(m, it.y + it.h), 0) || 1
      const maxCols = (layouts[currentBp] || []).reduce((m, it) => Math.max(m, it.x + it.w), 0) || 1

      const rh = Math.floor(
        (availH - marginY * Math.max(0, maxRows - 1)) / maxRows,
      )
      const cw = Math.floor(
        (availW - marginX * Math.max(0, maxCols - 1)) / maxCols,
      )
      const finalRh = Math.max(36, Math.min(rh, cw))
      setRowHeight(finalRh)

      // Enable outer scroll if total height > viewport on xs
      if (isXs && maxRows * finalRh > window.innerHeight - 43) {
        setContainerHeight(`${maxRows * finalRh + marginY * (maxRows - 1) + 100}px`)
      } else {
        setContainerHeight('calc(100vh - 43px)')
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [layouts, isXs])

  // Re-calc on resize/rotate
  React.useEffect(() => {
    const handleResize = () => setRowHeight(prev => prev)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const compactType = isXs ? 'vertical' : undefined

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        height: containerHeight,
        minHeight: 'calc(100vh - 43px)',
        width: '100%',
        p: { xs: 0.5, md: 2 }, 
        pb: { xs: 1, md: 3 },
        bgcolor: '#0b1220',
        color: 'white',
        overflowY: 'auto', 
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* background image layer */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
          opacity: { xs: 0.7, md: 1 },
        }}
      />
      {/* background aurora blobs */}
      {!isXs && (
        <>
          <Box
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              top: -120,
              left: -160,
              height: 640,
              width: 640,
              borderRadius: '50%',
              filter: 'blur(60px)',
              opacity: 0.4,
              background: 'radial-gradient(closest-side, rgba(56,189,248,0.35), transparent)',
            }}
          />
          <Box
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              bottom: -160,
              right: -120,
              height: 736,
              width: 736,
              borderRadius: '50%',
              filter: 'blur(60px)',
              opacity: 0.4,
              background: 'radial-gradient(closest-side, rgba(14,165,233,0.30), transparent)',
            }}
          />
        </>
      )}

      <GlobalStyles
        styles={{
          '.react-grid-item': {
            transition: 'transform 200ms cubic-bezier(0.2, 0, 0, 1), width 200ms, height 200ms',
          },
          '.react-grid-item.react-grid-placeholder': {
            background: 'rgba(255,255,255,0.15)',
          },
          '.react-resizable-handle': {
            display: isXs ? 'none' : 'block', // Hide resize on phone
            position: 'absolute',
            width: 12,
            height: 12,
            bottom: 8,
            right: 8,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.4))',
            boxShadow: '0 1px 3px rgba(0,0,0,0.35) inset',
            cursor: 'se-resize',
          },
          body: { overscrollBehaviorY: 'contain' }, // Prevent pull-to-refresh issues
        }}
      />

      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ xl: 1280, md: 900, xs: 0 }}
        cols={{ xl: 12, md: 12, xs: 12 }}
        rowHeight={rowHeight}
        margin={[isXs ? 4 : 8, isXs ? 4 : 8]}
        containerPadding={[isXs ? 4 : 8, isXs ? 4 : 8]}
        isDraggable={!isXs} // Disable drag on phone
        isResizable={!isXs}
        draggableHandle=".drag-handle"
        draggableCancel=".nodrag"
        compactType={compactType}
        preventCollision={!isXs}
        useCSSTransforms={true}
        onLayoutChange={(_current: Layout[], all: Layouts) => {
          if (!isXs) {
            setLayouts(all)
            try {
              localStorage.setItem('cisgenx-dashboard-layouts', JSON.stringify(all))
            } catch {}
          }
          // Re-calc rowHeight after change (non-xs)
          if (containerRef.current && !isXs) {
            const el = containerRef.current
            const styles = window.getComputedStyle(el)
            const padTop = parseFloat(styles.paddingTop || '0') || 0
            const padBottom = parseFloat(styles.paddingBottom || '0') || 0
            const gridPadY = 8 * 2
            const availH = Math.max(0, el.clientHeight - padTop - padBottom - gridPadY)
            const availW = el.clientWidth - 16
            const marginY = 8
            const marginX = 8
            const maxRows = (all.xl || []).reduce((m, it) => Math.max(m, it.y + it.h), 0) || 1
            const maxCols = (all.xl || []).reduce((m, it) => Math.max(m, it.x + it.w), 0) || 1
            const rh = Math.floor((availH - marginY * Math.max(0, maxRows - 1)) / maxRows)
            const cw = Math.floor((availW - marginX * Math.max(0, maxCols - 1)) / maxCols)
            const finalRh = Math.max(36, Math.min(rh, cw))
            setRowHeight(finalRh)
          }
        }}
      >
        <div key="radar" style={{ height: '100%', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <RadarIframeCard />
          </Box>
        </div>
        <div key="ws1" style={{ height: '100%', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            {liveStations && liveStations.length > 0 ? (
              <WeatherStationCard station={liveStations[0]} />
            ) : (
              <Box sx={{ p: 2, color: 'rgba(255,255,255,0.7)' }}>Loading…</Box>
            )}
          </Box>
        </div>
        <div key="ws2" style={{ height: '100%', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            {liveStations && liveStations.length > 1 ? (
              <WeatherStationCard station={liveStations[1]} />
            ) : (
              <Box sx={{ p: 2, color: 'rgba(255,255,255,0.7)' }}>Loading…</Box>
            )}
          </Box>
        </div>
        <div key="ws3" style={{ height: '100%', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            {liveStations && liveStations.length > 2 ? (
              <WeatherStationCard station={liveStations[2]} />
            ) : (
              <Box sx={{ p: 2, color: 'rgba(255,255,255,0.7)' }}>Loading…</Box>
            )}
          </Box>
        </div>
        <div key="soil" style={{ height: '100%', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <SoilMoistureHeatmapCard />
          </Box>
        </div>
      </ResponsiveGridLayout>
    </Box>
  )
}

// ---------------- Dev Self-Tests (run manually) ----------------
export function __runDashboardSelfTests() {
  console.assert(
    formatMaybe(1.234, '°C') === '1.2°C',
    'formatMaybe should round to 1 decimal',
  )
  console.assert(
    formatMaybe(undefined, '°C') === undefined,
    'formatMaybe should return undefined for missing value',
  )
}
